<script setup lang="ts">
import type { TabletIdentifiers } from '@/types/types'

const TABLET_HELP_URL =
  'https://heypongohelp.zendesk.com/hc/fr/articles/21925971964305-Utiliser-l-application-tablette-en-magasin'

const { isDirection } = useAccess()
const { displayTabletCode } = useTablet()

const props = defineProps<{
  tabletIdentifiers: TabletIdentifiers
}>()

const passwordLength = computed(() => props.tabletIdentifiers?.code?.length ?? 0)

const emit = defineEmits(['onRegenerateCodeRequest'])
const { t } = useI18n()

const isRegenerateModalOpen = ref(false)

const handleRegenerateCode = () => {
  isRegenerateModalOpen.value = false
  emit('onRegenerateCodeRequest')
}

const handleDisplayTabletCode = () => {
  displayTabletCode.value = !displayTabletCode.value
}
</script>

<template>
  <div class="mb-6 rounded-md border bg-white p-4">
    <div class="flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
      <div v-if="tabletIdentifiers">
        <p class="mb-3 font-bold text-foreground">
          {{ t('pages.marketplace.tabletsPage.identity.title') }}
        </p>
        <p class="text-sm text-muted-foreground">
          {{ t('pages.marketplace.tabletsPage.identity.mail') }}
          <span class="font-semibold text-foreground">{{ tabletIdentifiers.email }}</span>
        </p>
        <div class="flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
          <span>{{ t('pages.marketplace.tabletsPage.identity.password') }}</span>
          <span v-if="displayTabletCode" class="font-semibold tracking-wide text-foreground">
            {{ tabletIdentifiers.code }}
          </span>
          <span v-else class="tracking-widest text-foreground" aria-hidden="true">{{
            '●'.repeat(passwordLength)
          }}</span>
          <Button variant="link" size="sm" class="px-0" @click="handleDisplayTabletCode">
            {{ t(`pages.marketplace.tabletsPage.identity.${displayTabletCode ? 'hide' : 'show'}`) }}
          </Button>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-2">
        <Button variant="outline" as-child>
          <a :href="TABLET_HELP_URL" target="_blank" rel="noopener noreferrer">
            {{ t('pages.marketplace.tabletsPage.identity.help') }}
          </a>
        </Button>

        <Button v-if="isDirection" variant="outline" @click="isRegenerateModalOpen = true">
          {{ t('pages.marketplace.tabletsPage.identity.newCode') }}
        </Button>
      </div>
    </div>
  </div>
  <BaseModal
    :is-open="isRegenerateModalOpen"
    :text-title="t('pages.marketplace.tabletsPage.identity.regenerateConfirm.title')"
    :text="t('pages.marketplace.tabletsPage.identity.regenerateConfirm.subtitle')"
    icon="HeroArrowPathIconOutline"
    icon-color="red"
    :link-text="t('pages.marketplace.tabletsPage.identity.regenerateCode')"
    :cancel="t('global.words.cancel')"
    @close="isRegenerateModalOpen = false"
    @click="handleRegenerateCode"
  />
</template>
